import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
    FolderOpen, 
    Plus, 
    Upload, 
    FileText, 
    Trash2, 
    Share2, 
    Download, 
    Eye, 
    Lock, 
    Clock, 
    Edit, 
    ArrowLeft,
    X,
    Copy,
    Link,
    Shield,
    Building2,
    Globe,
    ExternalLink,
    Bookmark
} from 'lucide-react';

const DOCUMENT_TYPES = {
    medical: { label: 'Medical Records', color: 'bg-red-100 text-red-800' },
    iep: { label: 'IEP/Education', color: 'bg-blue-100 text-blue-800' },
    legal: { label: 'Legal Documents', color: 'bg-purple-100 text-purple-800' },
    insurance: { label: 'Insurance', color: 'bg-green-100 text-green-800' },
    therapy: { label: 'Therapy Records', color: 'bg-yellow-100 text-yellow-800' },
    school: { label: 'School Records', color: 'bg-orange-100 text-orange-800' },
    other: { label: 'Other', color: 'bg-gray-100 text-gray-800' }
};

const DigitalBinderPage = () => {
    const { user, isAuthenticated, login } = useAuth();
    const [binders, setBinders] = useState([]);
    const [selectedBinder, setSelectedBinder] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [shareLinks, setShareLinks] = useState([]);
    const [savedResources, setSavedResources] = useState([]);
    const [bookmarks, setBookmarks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('documents');
    
    // Dialog states
    const [showNewBinder, setShowNewBinder] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [showShare, setShowShare] = useState(false);
    const [showEditBinder, setShowEditBinder] = useState(false);
    
    // Form states
    const [newBinderForm, setNewBinderForm] = useState({ name: '', description: '', loved_one_name: '', relationship: '' });
    const [uploadForm, setUploadForm] = useState({ name: '', description: '', document_type: 'other', file: null });
    const [shareForm, setShareForm] = useState({ expires_in_days: '', password: '', access_type: 'view', document_ids: [] });
    const [uploadProgress, setUploadProgress] = useState(0);

    // Fetch binders
    const fetchBinders = useCallback(async () => {
        try {
            const response = await api.get('/binders');
            setBinders(response.data);
        } catch (error) {
            console.error('Error fetching binders:', error);
            toast.error('Failed to load binders');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch binder details
    const fetchBinderDetails = useCallback(async (binderId) => {
        try {
            const response = await api.get(`/binders/${binderId}`);
            setSelectedBinder(response.data.binder);
            setDocuments(response.data.documents);
            
            // Separate saved resources from bookmarks
            const allSaved = response.data.saved_resources || [];
            setSavedResources(allSaved.filter(r => r.type !== 'bookmark'));
            setBookmarks(allSaved.filter(r => r.type === 'bookmark'));
            
            // Also fetch share links
            const sharesResponse = await api.get(`/binders/${binderId}/shares`);
            setShareLinks(sharesResponse.data);
        } catch (error) {
            console.error('Error fetching binder details:', error);
            toast.error('Failed to load binder details');
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchBinders();
        } else {
            setLoading(false);
        }
    }, [isAuthenticated, fetchBinders]);

    // Create new binder
    const handleCreateBinder = async (e) => {
        e.preventDefault();
        try {
            const response = await api.post('/binders', newBinderForm);
            setBinders([response.data, ...binders]);
            setShowNewBinder(false);
            setNewBinderForm({ name: '', description: '', loved_one_name: '', relationship: '' });
            toast.success('Binder created successfully');
        } catch (error) {
            console.error('Error creating binder:', error);
            toast.error('Failed to create binder');
        }
    };

    // Update binder
    const handleUpdateBinder = async (e) => {
        e.preventDefault();
        try {
            const response = await api.put(`/binders/${selectedBinder.binder_id}`, newBinderForm);
            setSelectedBinder(response.data);
            setBinders(binders.map(b => b.binder_id === selectedBinder.binder_id ? response.data : b));
            setShowEditBinder(false);
            toast.success('Binder updated successfully');
        } catch (error) {
            console.error('Error updating binder:', error);
            toast.error('Failed to update binder');
        }
    };

    // Delete binder
    const handleDeleteBinder = async (binderId) => {
        if (!window.confirm('Are you sure you want to delete this binder and all its documents? This action cannot be undone.')) {
            return;
        }
        try {
            await api.delete(`/binders/${binderId}`);
            setBinders(binders.filter(b => b.binder_id !== binderId));
            if (selectedBinder?.binder_id === binderId) {
                setSelectedBinder(null);
                setDocuments([]);
            }
            toast.success('Binder deleted successfully');
        } catch (error) {
            console.error('Error deleting binder:', error);
            toast.error('Failed to delete binder');
        }
    };

    // Upload document
    const handleUploadDocument = async (e) => {
        e.preventDefault();
        if (!uploadForm.file) {
            toast.error('Please select a file');
            return;
        }

        try {
            setUploadProgress(10);
            
            // Read file as base64
            const reader = new FileReader();
            reader.onload = async (event) => {
                setUploadProgress(50);
                
                const fileData = event.target.result;
                const payload = {
                    name: uploadForm.name || uploadForm.file.name,
                    description: uploadForm.description,
                    document_type: uploadForm.document_type,
                    file_data: fileData,
                    file_name: uploadForm.file.name,
                    file_type: uploadForm.file.type
                };

                try {
                    const response = await api.post(`/binders/${selectedBinder.binder_id}/documents`, payload);
                    setDocuments([response.data, ...documents]);
                    setShowUpload(false);
                    setUploadForm({ name: '', description: '', document_type: 'other', file: null });
                    setUploadProgress(100);
                    toast.success('Document uploaded successfully');
                } catch (error) {
                    console.error('Error uploading document:', error);
                    toast.error(error.response?.data?.detail || 'Failed to upload document');
                }
                setUploadProgress(0);
            };
            
            reader.onerror = () => {
                toast.error('Error reading file');
                setUploadProgress(0);
            };
            
            reader.readAsDataURL(uploadForm.file);
        } catch (error) {
            console.error('Error uploading document:', error);
            toast.error('Failed to upload document');
            setUploadProgress(0);
        }
    };

    // Delete document
    const handleDeleteDocument = async (documentId) => {
        if (!window.confirm('Are you sure you want to delete this document?')) {
            return;
        }
        try {
            await api.delete(`/binders/${selectedBinder.binder_id}/documents/${documentId}`);
            setDocuments(documents.filter(d => d.document_id !== documentId));
            toast.success('Document deleted successfully');
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.error('Failed to delete document');
        }
    };

    // Download document
    const handleDownloadDocument = async (document) => {
        try {
            const response = await api.get(`/binders/${selectedBinder.binder_id}/documents/${document.document_id}`);
            const docData = response.data;
            
            // Create download link
            const link = document.createElement ? document.createElement('a') : window.document.createElement('a');
            link.href = docData.file_data;
            link.download = docData.file_name;
            link.click();
            
            toast.success('Download started');
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.error('Failed to download document');
        }
    };

    // Create share link
    const handleCreateShareLink = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...shareForm,
                expires_in_days: shareForm.expires_in_days ? parseInt(shareForm.expires_in_days) : null,
                password: shareForm.password || null
            };
            
            const response = await api.post(`/binders/${selectedBinder.binder_id}/share`, payload);
            setShareLinks([response.data.share_link, ...shareLinks]);
            setShowShare(false);
            setShareForm({ expires_in_days: '', password: '', access_type: 'view', document_ids: [] });
            
            // Copy link to clipboard
            const shareUrl = `${window.location.origin}${response.data.share_url}`;
            navigator.clipboard.writeText(shareUrl);
            toast.success('Share link created and copied to clipboard!');
        } catch (error) {
            console.error('Error creating share link:', error);
            toast.error('Failed to create share link');
        }
    };

    // Revoke share link
    const handleRevokeShare = async (shareId) => {
        if (!window.confirm('Are you sure you want to revoke this share link?')) {
            return;
        }
        try {
            await api.delete(`/shares/${shareId}`);
            setShareLinks(shareLinks.filter(s => s.share_id !== shareId));
            toast.success('Share link revoked');
        } catch (error) {
            console.error('Error revoking share:', error);
            toast.error('Failed to revoke share link');
        }
    };

    // Copy share link
    const copyShareLink = (shareId) => {
        const shareUrl = `${window.location.origin}/shared/${shareId}`;
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard');
    };

    // Remove saved resource from binder
    const handleRemoveSavedResource = async (resourceId) => {
        if (!window.confirm('Remove this resource from your binder?')) return;
        try {
            await api.delete(`/binders/${selectedBinder.binder_id}/resources/${resourceId}`);
            setSavedResources(savedResources.filter(r => r.resource_id !== resourceId));
            toast.success('Resource removed from binder');
        } catch (error) {
            console.error('Error removing resource:', error);
            toast.error('Failed to remove resource');
        }
    };

    // Remove bookmark from binder
    const handleRemoveBookmark = async (savedId) => {
        if (!window.confirm('Remove this bookmark from your binder?')) return;
        try {
            await api.delete(`/binders/${selectedBinder.binder_id}/bookmarks/${savedId}`);
            setBookmarks(bookmarks.filter(b => b.saved_id !== savedId));
            toast.success('Bookmark removed from binder');
        } catch (error) {
            console.error('Error removing bookmark:', error);
            toast.error('Failed to remove bookmark');
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Format date
    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="binder-login-prompt">
                <Card className="max-w-md w-full mx-4">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                            <Shield className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle>Digital Binder</CardTitle>
                        <CardDescription>
                            Securely store and share important documents for your IDD loved ones.
                            Please log in to access this feature.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button onClick={login} className="w-full" data-testid="login-to-binder-btn">
                            Member Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    // Binder detail view
    if (selectedBinder) {
        return (
            <div className="container max-w-6xl mx-auto py-8 px-4" data-testid="binder-detail-view">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <Button 
                        variant="ghost" 
                        onClick={() => { setSelectedBinder(null); setDocuments([]); setSavedResources([]); setBookmarks([]); }}
                        data-testid="back-to-binders-btn"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Binders
                    </Button>
                </div>

                <Card className="mb-6">
                    <CardHeader>
                        <div className="flex items-start justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2" data-testid="binder-title">
                                    <FolderOpen className="h-6 w-6 text-primary" />
                                    {selectedBinder.name}
                                </CardTitle>
                                {selectedBinder.loved_one_name && (
                                    <p className="text-muted-foreground mt-1">
                                        For: {selectedBinder.loved_one_name}
                                        {selectedBinder.relationship && ` (${selectedBinder.relationship})`}
                                    </p>
                                )}
                                {selectedBinder.description && (
                                    <CardDescription className="mt-2">{selectedBinder.description}</CardDescription>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => {
                                        setNewBinderForm({
                                            name: selectedBinder.name,
                                            description: selectedBinder.description || '',
                                            loved_one_name: selectedBinder.loved_one_name || '',
                                            relationship: selectedBinder.relationship || ''
                                        });
                                        setShowEditBinder(true);
                                    }}
                                    data-testid="edit-binder-btn"
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="text-destructive hover:bg-destructive/10"
                                    onClick={() => handleDeleteBinder(selectedBinder.binder_id)}
                                    data-testid="delete-binder-btn"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                </Card>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <div className="flex items-center justify-between mb-4">
                        <TabsList>
                            <TabsTrigger value="documents" data-testid="documents-tab">
                                <FileText className="h-4 w-4 mr-2" />
                                Documents ({documents.length})
                            </TabsTrigger>
                            <TabsTrigger value="resources" data-testid="resources-tab">
                                <Bookmark className="h-4 w-4 mr-2" />
                                Saved ({savedResources.length + bookmarks.length})
                            </TabsTrigger>
                            <TabsTrigger value="sharing" data-testid="sharing-tab">
                                <Share2 className="h-4 w-4 mr-2" />
                                Sharing ({shareLinks.length})
                            </TabsTrigger>
                        </TabsList>

                        {activeTab === 'documents' ? (
                            <Button onClick={() => setShowUpload(true)} data-testid="upload-document-btn">
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Document
                            </Button>
                        ) : (
                            <Button onClick={() => setShowShare(true)} data-testid="create-share-btn">
                                <Link className="h-4 w-4 mr-2" />
                                Create Share Link
                            </Button>
                        )}
                    </div>

                    <TabsContent value="documents">
                        {documents.length === 0 ? (
                            <Card className="text-center py-12" data-testid="no-documents-message">
                                <CardContent>
                                    <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground mb-4">No documents yet</p>
                                    <Button onClick={() => setShowUpload(true)}>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Upload First Document
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid gap-4">
                                {documents.map((doc) => (
                                    <Card key={doc.document_id} data-testid={`document-${doc.document_id}`}>
                                        <CardContent className="flex items-center justify-between py-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                                                    <FileText className="h-6 w-6 text-muted-foreground" />
                                                </div>
                                                <div>
                                                    <h4 className="font-medium">{doc.name}</h4>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <Badge className={DOCUMENT_TYPES[doc.document_type]?.color || DOCUMENT_TYPES.other.color}>
                                                            {DOCUMENT_TYPES[doc.document_type]?.label || 'Other'}
                                                        </Badge>
                                                        <span className="text-sm text-muted-foreground">
                                                            {formatFileSize(doc.file_size)}
                                                        </span>
                                                        <span className="text-sm text-muted-foreground">
                                                            {formatDate(doc.created_at)}
                                                        </span>
                                                    </div>
                                                    {doc.description && (
                                                        <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => handleDownloadDocument(doc)}
                                                    data-testid={`download-${doc.document_id}`}
                                                >
                                                    <Download className="h-4 w-4" />
                                                </Button>
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    className="text-destructive hover:bg-destructive/10"
                                                    onClick={() => handleDeleteDocument(doc.document_id)}
                                                    data-testid={`delete-doc-${doc.document_id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Saved Resources & Bookmarks Tab */}
                    <TabsContent value="resources">
                        {savedResources.length === 0 && bookmarks.length === 0 ? (
                            <Card className="text-center py-12" data-testid="no-saved-resources">
                                <CardContent>
                                    <Bookmark className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground mb-2">No saved items yet</p>
                                    <p className="text-sm text-muted-foreground">
                                        Browse the <a href="/directory" className="text-primary hover:underline">Public Directory</a> or <a href="/news" className="text-primary hover:underline">Resource Library</a> and save items to this binder.
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-6">
                                {/* Directory Resources */}
                                {savedResources.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                                            <Building2 className="h-4 w-4" />
                                            Public Directory Resources ({savedResources.length})
                                        </h3>
                                        <div className="grid gap-3">
                                            {savedResources.map((res) => (
                                                <Card key={res.saved_id} data-testid={`saved-resource-${res.resource_id}`}>
                                                    <CardContent className="flex items-center justify-between py-4">
                                                        <div className="flex items-center gap-4 min-w-0 flex-1">
                                                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                                                                <Building2 className="h-6 w-6 text-primary" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <a 
                                                                    href={`/resource/${res.resource_id}`}
                                                                    className="font-medium hover:text-primary transition-colors truncate block"
                                                                    data-testid={`resource-link-${res.resource_id}`}
                                                                >
                                                                    {res.resource_name}
                                                                </a>
                                                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                                    {res.resource_state && (
                                                                        <Badge variant="outline" className="text-xs">
                                                                            {res.resource_city ? `${res.resource_city}, ` : ''}{res.resource_state}
                                                                        </Badge>
                                                                    )}
                                                                    {res.resource_types?.slice(0, 2).map((t, i) => (
                                                                        <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>
                                                                    ))}
                                                                </div>
                                                                {res.notes && (
                                                                    <p className="text-sm text-muted-foreground mt-1 italic">"{res.notes}"</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2 shrink-0 ml-4">
                                                            {res.resource_website && (
                                                                <Button 
                                                                    variant="outline" 
                                                                    size="sm"
                                                                    asChild
                                                                >
                                                                    <a href={res.resource_website} target="_blank" rel="noopener noreferrer" data-testid={`website-${res.resource_id}`}>
                                                                        <ExternalLink className="h-4 w-4" />
                                                                    </a>
                                                                </Button>
                                                            )}
                                                            <Button 
                                                                variant="outline" 
                                                                size="sm"
                                                                className="text-destructive hover:bg-destructive/10"
                                                                onClick={() => handleRemoveSavedResource(res.resource_id)}
                                                                data-testid={`remove-resource-${res.resource_id}`}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Bookmarked Articles */}
                                {bookmarks.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                                            <Globe className="h-4 w-4" />
                                            Saved Articles & Links ({bookmarks.length})
                                        </h3>
                                        <div className="grid gap-3">
                                            {bookmarks.map((bm) => (
                                                <Card key={bm.saved_id} data-testid={`bookmark-${bm.saved_id}`}>
                                                    <CardContent className="flex items-center justify-between py-4">
                                                        <div className="flex items-center gap-4 min-w-0 flex-1">
                                                            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                                                                <Globe className="h-6 w-6 text-blue-600" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <a 
                                                                    href={bm.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="font-medium hover:text-primary transition-colors line-clamp-2 block"
                                                                    data-testid={`bookmark-link-${bm.saved_id}`}
                                                                >
                                                                    {bm.title}
                                                                </a>
                                                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                                    {bm.source && (
                                                                        <Badge variant="outline" className="text-xs">{bm.source}</Badge>
                                                                    )}
                                                                    {bm.category && (
                                                                        <Badge variant="secondary" className="text-xs">{bm.category}</Badge>
                                                                    )}
                                                                    <span className="text-xs text-muted-foreground">
                                                                        {formatDate(bm.saved_at)}
                                                                    </span>
                                                                </div>
                                                                {bm.notes && (
                                                                    <p className="text-sm text-muted-foreground mt-1 italic">"{bm.notes}"</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2 shrink-0 ml-4">
                                                            <Button 
                                                                variant="outline" 
                                                                size="sm"
                                                                asChild
                                                            >
                                                                <a href={bm.url} target="_blank" rel="noopener noreferrer" data-testid={`open-bookmark-${bm.saved_id}`}>
                                                                    <ExternalLink className="h-4 w-4" />
                                                                </a>
                                                            </Button>
                                                            <Button 
                                                                variant="outline" 
                                                                size="sm"
                                                                className="text-destructive hover:bg-destructive/10"
                                                                onClick={() => handleRemoveBookmark(bm.saved_id)}
                                                                data-testid={`remove-bookmark-${bm.saved_id}`}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="sharing">
                        {shareLinks.length === 0 ? (
                            <Card className="text-center py-12" data-testid="no-shares-message">
                                <CardContent>
                                    <Share2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground mb-4">No share links yet</p>
                                    <Button onClick={() => setShowShare(true)}>
                                        <Link className="h-4 w-4 mr-2" />
                                        Create First Share Link
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid gap-4">
                                {shareLinks.map((share) => (
                                    <Card key={share.share_id} data-testid={`share-${share.share_id}`}>
                                        <CardContent className="flex items-center justify-between py-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                                                    <Link className="h-6 w-6 text-muted-foreground" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant={share.is_active ? "default" : "secondary"}>
                                                            {share.is_active ? "Active" : "Revoked"}
                                                        </Badge>
                                                        <Badge variant="outline">
                                                            {share.access_type === 'download' ? (
                                                                <><Download className="h-3 w-3 mr-1" /> Download</>
                                                            ) : (
                                                                <><Eye className="h-3 w-3 mr-1" /> View Only</>
                                                            )}
                                                        </Badge>
                                                        {share.password_hash && (
                                                            <Badge variant="outline">
                                                                <Lock className="h-3 w-3 mr-1" /> Password Protected
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                                        <span>Created: {formatDate(share.created_at)}</span>
                                                        {share.expires_at && (
                                                            <span className="flex items-center gap-1">
                                                                <Clock className="h-3 w-3" />
                                                                Expires: {formatDate(share.expires_at)}
                                                            </span>
                                                        )}
                                                        <span>Views: {share.view_count}{share.max_views ? `/${share.max_views}` : ''}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                {share.is_active && (
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => copyShareLink(share.share_id)}
                                                        data-testid={`copy-share-${share.share_id}`}
                                                    >
                                                        <Copy className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                {share.is_active && (
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        className="text-destructive hover:bg-destructive/10"
                                                        onClick={() => handleRevokeShare(share.share_id)}
                                                        data-testid={`revoke-share-${share.share_id}`}
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>

                {/* Upload Document Dialog */}
                <Dialog open={showUpload} onOpenChange={setShowUpload}>
                    <DialogContent data-testid="upload-dialog">
                        <DialogHeader>
                            <DialogTitle>Upload Document</DialogTitle>
                            <DialogDescription>
                                Upload a document to your binder. Supported: PDF, images, Word docs (max 50MB)
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleUploadDocument}>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="file">File</Label>
                                    <Input
                                        id="file"
                                        type="file"
                                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp"
                                        onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                                        data-testid="file-input"
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="doc-name">Document Name</Label>
                                    <Input
                                        id="doc-name"
                                        value={uploadForm.name}
                                        onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                                        placeholder="Optional - defaults to filename"
                                        data-testid="doc-name-input"
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="doc-type">Document Type</Label>
                                    <Select 
                                        value={uploadForm.document_type} 
                                        onValueChange={(value) => setUploadForm({ ...uploadForm, document_type: value })}
                                    >
                                        <SelectTrigger data-testid="doc-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(DOCUMENT_TYPES).map(([key, { label }]) => (
                                                <SelectItem key={key} value={key}>{label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="doc-desc">Description</Label>
                                    <Textarea
                                        id="doc-desc"
                                        value={uploadForm.description}
                                        onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                                        placeholder="Optional notes about this document"
                                        data-testid="doc-desc-input"
                                    />
                                </div>
                                {uploadProgress > 0 && uploadProgress < 100 && (
                                    <div className="w-full bg-muted rounded-full h-2">
                                        <div 
                                            className="bg-primary h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setShowUpload(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={!uploadForm.file || uploadProgress > 0} data-testid="submit-upload-btn">
                                    <Upload className="h-4 w-4 mr-2" />
                                    Upload
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Create Share Link Dialog */}
                <Dialog open={showShare} onOpenChange={setShowShare}>
                    <DialogContent data-testid="share-dialog">
                        <DialogHeader>
                            <DialogTitle>Create Share Link</DialogTitle>
                            <DialogDescription>
                                Create a secure link to share this binder with healthcare providers, schools, or family members.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreateShareLink}>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="access-type">Access Type</Label>
                                    <Select 
                                        value={shareForm.access_type} 
                                        onValueChange={(value) => setShareForm({ ...shareForm, access_type: value })}
                                    >
                                        <SelectTrigger data-testid="access-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="view">
                                                <span className="flex items-center gap-2">
                                                    <Eye className="h-4 w-4" /> View Only
                                                </span>
                                            </SelectItem>
                                            <SelectItem value="download">
                                                <span className="flex items-center gap-2">
                                                    <Download className="h-4 w-4" /> View & Download
                                                </span>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="expires">Expires In (Days)</Label>
                                    <Input
                                        id="expires"
                                        type="number"
                                        min="1"
                                        max="365"
                                        value={shareForm.expires_in_days}
                                        onChange={(e) => setShareForm({ ...shareForm, expires_in_days: e.target.value })}
                                        placeholder="Optional - leave blank for no expiration"
                                        data-testid="expires-input"
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="password">Password Protection</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={shareForm.password}
                                        onChange={(e) => setShareForm({ ...shareForm, password: e.target.value })}
                                        placeholder="Optional - add a password for extra security"
                                        data-testid="share-password-input"
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setShowShare(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" data-testid="create-share-link-btn">
                                    <Link className="h-4 w-4 mr-2" />
                                    Create Link
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Edit Binder Dialog */}
                <Dialog open={showEditBinder} onOpenChange={setShowEditBinder}>
                    <DialogContent data-testid="edit-binder-dialog">
                        <DialogHeader>
                            <DialogTitle>Edit Binder</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleUpdateBinder}>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="edit-name">Binder Name *</Label>
                                    <Input
                                        id="edit-name"
                                        value={newBinderForm.name}
                                        onChange={(e) => setNewBinderForm({ ...newBinderForm, name: e.target.value })}
                                        required
                                        data-testid="edit-binder-name-input"
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="edit-loved-one">For (Loved One's Name)</Label>
                                    <Input
                                        id="edit-loved-one"
                                        value={newBinderForm.loved_one_name}
                                        onChange={(e) => setNewBinderForm({ ...newBinderForm, loved_one_name: e.target.value })}
                                        placeholder="Name of your IDD loved one"
                                        data-testid="edit-loved-one-input"
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="edit-relationship">Your Relationship</Label>
                                    <Select 
                                        value={newBinderForm.relationship} 
                                        onValueChange={(value) => setNewBinderForm({ ...newBinderForm, relationship: value })}
                                    >
                                        <SelectTrigger data-testid="edit-relationship-select">
                                            <SelectValue placeholder="Select relationship" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Parent">Parent</SelectItem>
                                            <SelectItem value="Sibling">Sibling</SelectItem>
                                            <SelectItem value="Guardian">Guardian</SelectItem>
                                            <SelectItem value="Caregiver">Caregiver</SelectItem>
                                            <SelectItem value="Other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="edit-desc">Description</Label>
                                    <Textarea
                                        id="edit-desc"
                                        value={newBinderForm.description}
                                        onChange={(e) => setNewBinderForm({ ...newBinderForm, description: e.target.value })}
                                        placeholder="Optional description"
                                        data-testid="edit-binder-desc-input"
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setShowEditBinder(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" data-testid="save-binder-btn">
                                    Save Changes
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    // Binders list view
    return (
        <div className="container max-w-6xl mx-auto py-8 px-4" data-testid="binders-list-view">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold">Digital Binder</h1>
                    <p className="text-muted-foreground mt-1">
                        Securely store and share important documents for your IDD loved ones
                    </p>
                </div>
                <Button onClick={() => setShowNewBinder(true)} data-testid="create-binder-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Binder
                </Button>
            </div>

            {/* Binders Grid */}
            {binders.length === 0 ? (
                <Card className="text-center py-16" data-testid="no-binders-message">
                    <CardContent>
                        <FolderOpen className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium mb-2">No binders yet</h3>
                        <p className="text-muted-foreground mb-6">
                            Create your first digital binder to start organizing important documents
                        </p>
                        <Button onClick={() => setShowNewBinder(true)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Create Your First Binder
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {binders.map((binder) => (
                        <Card 
                            key={binder.binder_id} 
                            className="cursor-pointer hover:shadow-md transition-shadow"
                            onClick={() => fetchBinderDetails(binder.binder_id)}
                            data-testid={`binder-card-${binder.binder_id}`}
                        >
                            <CardHeader>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                                            <FolderOpen className="h-6 w-6 text-primary" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">{binder.name}</CardTitle>
                                            {binder.loved_one_name && (
                                                <p className="text-sm text-muted-foreground">
                                                    For: {binder.loved_one_name}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {binder.description && (
                                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                                        {binder.description}
                                    </p>
                                )}
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span>Created {formatDate(binder.created_at)}</span>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Create Binder Dialog */}
            <Dialog open={showNewBinder} onOpenChange={setShowNewBinder}>
                <DialogContent data-testid="create-binder-dialog">
                    <DialogHeader>
                        <DialogTitle>Create New Binder</DialogTitle>
                        <DialogDescription>
                            Create a digital binder to organize important documents for your loved one.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateBinder}>
                        <div className="grid gap-4 py-4">
                            <div className="grid gap-2">
                                <Label htmlFor="name">Binder Name *</Label>
                                <Input
                                    id="name"
                                    value={newBinderForm.name}
                                    onChange={(e) => setNewBinderForm({ ...newBinderForm, name: e.target.value })}
                                    placeholder="e.g., Medical Records, School Documents"
                                    required
                                    data-testid="binder-name-input"
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="loved-one">For (Loved One's Name)</Label>
                                <Input
                                    id="loved-one"
                                    value={newBinderForm.loved_one_name}
                                    onChange={(e) => setNewBinderForm({ ...newBinderForm, loved_one_name: e.target.value })}
                                    placeholder="Name of your IDD loved one"
                                    data-testid="loved-one-input"
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="relationship">Your Relationship</Label>
                                <Select 
                                    value={newBinderForm.relationship} 
                                    onValueChange={(value) => setNewBinderForm({ ...newBinderForm, relationship: value })}
                                >
                                    <SelectTrigger data-testid="relationship-select">
                                        <SelectValue placeholder="Select relationship" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Parent">Parent</SelectItem>
                                        <SelectItem value="Sibling">Sibling</SelectItem>
                                        <SelectItem value="Guardian">Guardian</SelectItem>
                                        <SelectItem value="Caregiver">Caregiver</SelectItem>
                                        <SelectItem value="Other">Other</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    value={newBinderForm.description}
                                    onChange={(e) => setNewBinderForm({ ...newBinderForm, description: e.target.value })}
                                    placeholder="Optional description of what this binder contains"
                                    data-testid="binder-desc-input"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowNewBinder(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={!newBinderForm.name} data-testid="submit-binder-btn">
                                Create Binder
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DigitalBinderPage;
